import { Search, LogOut, Sparkles, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { NotificationDropdown } from "@/components/NotificationDropdown";
import { useAuthStore } from "@/store/auth";
import { toast } from "sonner";
import { useLogout } from "@/features/auth/hooks/useAuth";
import { PATHS } from "@/lib/paths";

export function TopBar() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const handleSignOut = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    logout();
  };

  const handleSettingsClick = () => {
    navigate(PATHS.settings);
  };

  const userInitials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("") || "AD";

  return (
    <header className="bg-gradient-to-r from-white via-slate-50/50 to-white border-b border-slate-200/60 px-4 sm:px-6 py-4 shadow-soft flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex ml-12 sm:ml-0 items-center space-x-2 sm:space-x-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-soft shrink-0">
              <Sparkles className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 bg-clip-text text-transparent">
              <span className="hidden sm:inline">Command Centre</span>
              <span className="sm:hidden">CC</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="relative group hidden md:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
            <Input
              placeholder="Search users, transactions..."
              className="pl-10 w-80 bg-white/80 border-slate-200/60 focus:border-primary/50 focus:bg-white transition-all duration-200 shadow-soft"
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="md:hidden w-8 h-8 p-0 hover:bg-slate-100/80 rounded-lg"
          >
            <Search className="w-4 h-4 text-slate-600" />
          </Button>

          {/* <NotificationDropdown /> */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center space-x-2 sm:space-x-3 hover:bg-slate-100/80 rounded-xl transition-all duration-200 hover:shadow-soft px-2 sm:px-3 py-2 h-auto"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-primary rounded-full flex items-center justify-center shadow-soft">
                  <span className="text-xs font-bold text-white">
                    {userInitials}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-semibold text-slate-700">
                    {user?.name || "Admin User"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {user?.role?.name
                      .replace("_", " ")
                      .split(" ")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white/95 border-slate-200/60 shadow-modern"
            >
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="hover:bg-slate-100/80 rounded-lg mx-1 transition-all duration-200 cursor-pointer"
                onClick={handleSettingsClick}
              >
                <Settings className="w-4 h-4 mr-2 text-slate-600" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger hover:bg-red-50/80 rounded-lg mx-1 transition-all duration-200 cursor-pointer focus:bg-red-50 focus:text-danger"
                onClick={handleSignOut}
                disabled={isLoggingOut}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? "Signing Out..." : "Sign Out"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
